var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef, useState } from "react";
import Button from "@mui/material/Button";
import { prepareZXingModule, readBarcodes } from "zxing-wasm/reader";
function parseScanResult(input) {
    // Legacy: Telegram bot format with player UUID
    const legacyPattern = /(?:\/start\s+|[?]start=)invite_code_([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;
    const legacyMatch = input.match(legacyPattern);
    if (legacyMatch === null || legacyMatch === void 0 ? void 0 : legacyMatch[1]) {
        return { type: 'playerId', value: legacyMatch[1] };
    }
    // New: web-app URL with ref code (e.g. https://kultmafia.ru/register?ref=7CHD4R)
    const refPattern = /[?&]ref=([A-Za-z0-9]+)/;
    const refMatch = input.match(refPattern);
    if (refMatch === null || refMatch === void 0 ? void 0 : refMatch[1]) {
        return { type: 'refCode', value: refMatch[1] };
    }
    return null;
}
const JoinQrCodeReader = ({ onPlayerFound, onRefCodeFound, onClose, }) => {
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const [result, setResult] = useState("No result");
    const [error, setError] = useState(null);
    const [isScanning, setIsScanning] = useState(true);
    const [facingMode, setFacingMode] = useState("environment");
    const [showSuccess, setShowSuccess] = useState(false);
    const streamRef = useRef(null);
    const rafRef = useRef(null);
    const frameSkipRef = useRef(0);
    const readyRef = useRef(false);
    // Store callbacks in refs to avoid re-triggering the camera useEffect
    const onPlayerFoundRef = useRef(onPlayerFound);
    onPlayerFoundRef.current = onPlayerFound;
    const onRefCodeFoundRef = useRef(onRefCodeFound);
    onRefCodeFoundRef.current = onRefCodeFound;
    const clearResult = () => {
        setResult("No result");
        setError(null);
        setIsScanning(true);
        setShowSuccess(false);
    };
    const toggleCamera = () => {
        setFacingMode((prev) => (prev === "environment" ? "user" : "environment"));
        setIsScanning(true);
        setResult("No result");
        setError(null);
        setShowSuccess(false);
    };
    const calculateBrightness = (imageData) => {
        let total = 0;
        const data = imageData.data;
        for (let i = 0; i < data.length; i += 4) {
            total += (data[i] + data[i + 1] + data[i + 2]) / 3;
        }
        return total / (imageData.width * imageData.height);
    };
    const stopAll = () => {
        var _a;
        if (rafRef.current) {
            cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
        }
        (_a = streamRef.current) === null || _a === void 0 ? void 0 : _a.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
    };
    useEffect(() => {
        const video = videoRef.current;
        const canvas = canvasRef.current;
        if (!video || !canvas)
            return;
        let brightnessTimer = null;
        const start = () => __awaiter(void 0, void 0, void 0, function* () {
            try {
                try {
                    yield prepareZXingModule();
                }
                catch (wasmErr) {
                    setError(`WASM: ${(wasmErr === null || wasmErr === void 0 ? void 0 : wasmErr.name) || 'Error'}: ${(wasmErr === null || wasmErr === void 0 ? void 0 : wasmErr.message) || String(wasmErr)}`);
                    setIsScanning(false);
                    return;
                }
                stopAll();
                const constraints = {
                    video: {
                        facingMode,
                        width: { ideal: 1280 },
                        height: { ideal: 720 },
                        frameRate: { ideal: 30, max: 30 },
                    },
                    audio: false,
                };
                let stream;
                try {
                    stream = yield navigator.mediaDevices.getUserMedia(constraints);
                }
                catch (camErr) {
                    setError(`Камера: ${(camErr === null || camErr === void 0 ? void 0 : camErr.name) || 'Error'}: ${(camErr === null || camErr === void 0 ? void 0 : camErr.message) || String(camErr)}`);
                    setIsScanning(false);
                    stopAll();
                    return;
                }
                streamRef.current = stream;
                video.srcObject = stream;
                yield video.play();
                const ctx = canvas.getContext("2d");
                if (!ctx) {
                    setError("Не удалось инициализировать контекст canvas");
                    setIsScanning(false);
                    return;
                }
                const scanLoop = () => __awaiter(void 0, void 0, void 0, function* () {
                    var _a, _b;
                    if (!isScanning || !videoRef.current || !canvasRef.current)
                        return;
                    rafRef.current = requestAnimationFrame(scanLoop);
                    frameSkipRef.current = (frameSkipRef.current + 1) % 2;
                    if (frameSkipRef.current !== 0)
                        return;
                    const v = videoRef.current;
                    const c = canvasRef.current;
                    const targetW = 1280;
                    const scale = Math.min(1, targetW / (v.videoWidth || targetW));
                    const w = Math.floor((v.videoWidth || targetW) * scale);
                    const h = Math.floor((v.videoHeight || 720) * scale);
                    c.width = w;
                    c.height = h;
                    ctx.drawImage(v, 0, 0, w, h);
                    const imageData = ctx.getImageData(0, 0, w, h);
                    if (!readyRef.current) {
                        const b = calculateBrightness(imageData);
                        if (b < 35) {
                            setResult("Слишком темно, улучшите освещение");
                            return;
                        }
                        readyRef.current = true;
                    }
                    try {
                        const out = yield readBarcodes(imageData, {
                            tryHarder: true,
                            formats: ["QRCode"],
                            maxNumberOfSymbols: 1,
                        });
                        if (out.length > 0) {
                            const text = out[0].text;
                            const parsed = parseScanResult(text);
                            if (parsed) {
                                setResult(text);
                                setIsScanning(false);
                                stopAll();
                                if (parsed.type === 'playerId') {
                                    (_a = onPlayerFoundRef.current) === null || _a === void 0 ? void 0 : _a.call(onPlayerFoundRef, parsed.value);
                                }
                                else {
                                    (_b = onRefCodeFoundRef.current) === null || _b === void 0 ? void 0 : _b.call(onRefCodeFoundRef, parsed.value);
                                }
                                setShowSuccess(true);
                            }
                            else {
                                setResult("QR-код не содержит валидный код игрока");
                            }
                        }
                    }
                    catch (_c) {
                        // игнорируем ошибки кадра
                    }
                });
                brightnessTimer = window.setInterval(() => {
                    if (!videoRef.current || !canvasRef.current)
                        return;
                    if (video.readyState === video.HAVE_ENOUGH_DATA) {
                        const c = canvasRef.current;
                        const ctx2 = c.getContext("2d");
                        if (!ctx2)
                            return;
                        c.width = 320;
                        c.height =
                            Math.floor((320 / video.videoWidth) * video.videoHeight) || 180;
                        ctx2.drawImage(video, 0, 0, c.width, c.height);
                        const img = ctx2.getImageData(0, 0, c.width, c.height);
                        const b = calculateBrightness(img);
                        if (b < 35)
                            setResult("Слишком темно, улучшите освещение");
                    }
                }, 1000);
                setError(null);
                scanLoop();
            }
            catch (err) {
                console.error("Ошибка запуска камеры/WASM:", err);
                setError("Ошибка доступа к камере или инициализации WASM. Проверьте разрешения.");
                setIsScanning(false);
                stopAll();
            }
        });
        if (isScanning)
            start();
        return () => {
            if (brightnessTimer)
                window.clearInterval(brightnessTimer);
            stopAll();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isScanning, facingMode]);
    const hasValidData = result !== "No result" &&
        result !== "QR-код не найден" &&
        result !== "Слишком темно, улучшите освещение" &&
        result !== "QR-код не содержит валидный код игрока";
    if (showSuccess) {
        return (_jsxs("div", Object.assign({ style: { maxWidth: "400px", margin: "0 auto", padding: "20px" } }, { children: [_jsx("h2", Object.assign({ style: { color: "white" } }, { children: "\u0418\u0433\u0440\u043E\u043A \u0434\u043E\u0431\u0430\u0432\u043B\u0435\u043D" })), _jsxs("div", Object.assign({ style: { display: "flex", gap: "10px", marginTop: "15px" } }, { children: [_jsx(Button, Object.assign({ variant: "contained", color: "primary", onClick: clearResult }, { children: "\u0414\u043E\u0431\u0430\u0432\u0438\u0442\u044C \u0435\u0449\u0435" })), _jsx(Button, Object.assign({ variant: "outlined", color: "secondary", onClick: onClose }, { children: "\u0417\u0430\u043A\u0440\u044B\u0442\u044C" }))] }))] })));
    }
    return (_jsxs("div", Object.assign({ style: { maxWidth: "400px", margin: "0 auto", padding: "20px" } }, { children: [_jsx("h2", Object.assign({ style: { color: "white" } }, { children: "\u0421\u043A\u0430\u043D\u0435\u0440 QR-\u043A\u043E\u0434\u0430" })), error ? (_jsx("p", Object.assign({ style: { color: "red" } }, { children: error }))) : (_jsxs(_Fragment, { children: [_jsxs("div", Object.assign({ style: { position: "relative" } }, { children: [_jsx("video", { ref: videoRef, playsInline: true, muted: true, style: {
                                    width: "100%",
                                    border: "2px solid #333",
                                    borderRadius: "8px",
                                } }), _jsx("div", { style: {
                                    position: "absolute",
                                    top: "50%",
                                    left: "50%",
                                    transform: "translate(-50%, -50%)",
                                    width: "60%",
                                    height: "60%",
                                    border: "3px solid #00ff00",
                                    borderRadius: "8px",
                                    pointerEvents: "none",
                                } })] })), _jsx("canvas", { ref: canvasRef, style: { display: "none" } }), _jsx("p", Object.assign({ style: { color: "white", fontSize: "15px" } }, { children: "\u041E\u0442\u0441\u043A\u0430\u043D\u0438\u0440\u0443\u0439\u0442\u0435 QR \u0438\u0437 \u043F\u0440\u0438\u043B\u043E\u0436\u0435\u043D\u0438\u044F \u0438\u0433\u0440\u043E\u043A\u0430" })), _jsxs("div", Object.assign({ style: { display: "flex", gap: "10px", marginTop: "15px" } }, { children: [hasValidData && (_jsx(Button, Object.assign({ variant: "contained", color: "primary", onClick: clearResult }, { children: "\u041E\u0447\u0438\u0441\u0442\u0438\u0442\u044C \u0440\u0435\u0437\u0443\u043B\u044C\u0442\u0430\u0442" }))), _jsx(Button, Object.assign({ variant: "outlined", color: "secondary", onClick: toggleCamera }, { children: "\u041F\u0435\u0440\u0435\u043A\u043B\u044E\u0447\u0438\u0442\u044C \u043A\u0430\u043C\u0435\u0440\u0443" }))] }))] }))] })));
};
export default JoinQrCodeReader;
